import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotebookPen, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNotes } from "./NotesContext";
import ProductPill from "./ProductPill";

export const NotesInboxDialog = () => {
  const { notes, deleteNote, identityFor } = useNotes();
  const [open, setOpen] = useState(false);

  const remove = async (id) => {
    try {
      await deleteNote(id);
      toast.success("Note removed");
    } catch (e) {
      toast.error("We couldn't remove that note.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="notes-inbox-button">
          <NotebookPen size={15} />
          <span>Your notes</span>
          {notes.length > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold"
              style={{ background: "rgba(15,118,110,0.12)", color: "#0F766E" }}
            >
              {notes.length}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="notes-inbox-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Your notes for the Master Planner</DialogTitle>
          <DialogDescription>
            Everything you've flagged. The Master Planner works through these and turns them into next steps.
          </DialogDescription>
        </DialogHeader>
        {notes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No notes yet. Click any point on the dashboard to leave one.
          </p>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-3">
            <div className="space-y-2.5">
              {notes.map((n) => (
                <div key={n.id} className="rounded-lg border p-3 product-identity-section" style={{ "--product-swatch": identityFor(n.product_slug)?.hex }} data-testid={`inbox-note-row-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {n.product_name && (
                        <div className="mb-1.5">
                          {identityFor(n.product_slug) ? (
                            <ProductPill product={identityFor(n.product_slug)} testId={`note-product-tag-${n.product_slug}-${n.id}`} />
                          ) : <span className="text-xs text-muted-foreground" data-testid={`note-product-label-${n.id}`}>{n.product_name}</span>}
                        </div>
                      )}
                      {n.item_text && (
                        <p className="text-[11px] text-muted-foreground leading-relaxed mb-1">On: {n.item_text}</p>
                      )}
                      <p className="text-sm text-foreground leading-relaxed font-medium">{n.note}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-8 w-8 p-0"
                      onClick={() => remove(n.id)}
                      data-testid="inbox-delete-note"
                      aria-label="Remove note"
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotesInboxDialog;
